import * as constants from '../AppConstants';
import {translations} from '../locale/translate/Translations';

import moment from 'moment';

/**
 * This class defines common utility functions used throughout the application.
 */
const symbolPrefix = 'Symbol(';

const serviceUrlParams = [
    'fromDate',
    'toDate',
    'interval',
    'calc',
    'farmId',
    'siteId',
    'gridId',
    'penId',
    'metricId',
    'metricTypeId',
    'metricCategoryId',
    'sensorId',
    'targetType',
    'max',
    'start',
    'severity',
    'alertType',
    'timeFrame',
    'vectorId'
];

class AppUtils {
    /**
     * Convert a class based on symbol properties to a json string.
     */
    static toJson(clsInst) {
        let symbolProps = this.getClassSymbolProps(clsInst);
        let json = {};

        symbolProps.forEach(prop => {
            let n = this.getSymbolPropName(prop);
            let v = clsInst[prop];
            json[n] = v;
        });

        return json;
    }

    /**
     * Assign values from a json object to a given instance of
     * a class based on symbol properties.
     */
    static fromJson(clsInst, json) {
        let symbolProps = this.getClassSymbolProps(clsInst);

        symbolProps.forEach(prop => {
            let n = this.getSymbolPropName(prop);
            let v = json[n];
            if (v) {
                clsInst[prop] = v;
            }
        });

        return clsInst;
    }

    /**
     * Get an array of symbol properties defined within
     * a specified class instance.
     */
    static getClassSymbolProps(clsInst) {
        let clsKeys = Reflect.ownKeys(clsInst);

        let symbolProps = [];

        for (var ix in clsKeys) {
            let prop = clsKeys[ix];
            let propName = prop.toString();
            if (propName.toString().startsWith(symbolPrefix)) {
                symbolProps.push(prop);
            }
        }

        return symbolProps;
    }

    /**
     * Get the textual value associated with a symbol.
     */
    static getSymbolPropName(symbolProp) {
        let propName = symbolProp.toString();
        return propName.slice(symbolPrefix.length, propName.length-1);
    }

    /**
     * Parse a given date string into a date object. The string
     * is assumed to be in "YYYY-MM-dd HH:mm:ss" format
     */
    static parseDate(dtStr) {
        if (dtStr === null || dtStr === undefined || dtStr.length === 0) {
            return undefined;
        }

        let dt = undefined;
        let dateValues = AppUtils.parseDateValues(dtStr);
        if (dateValues.year && dateValues.month >= 0 && dateValues.day) {
            dt = new Date();
            dt.setFullYear(dateValues.year);
            dt.setMonth(dateValues.month);
            dt.setDate(dateValues.day);
            dt.setHours(0);
            dt.setMinutes(0);
            dt.setSeconds(0);

            if (dateValues.hours) {
                dt.setHours(dateValues.hours);
                if (dateValues.minutes) {
                    dt.setMinutes(dateValues.minutes);
                    if (dateValues.seconds) {
                        dt.setSeconds(dateValues.seconds);
                    }
                }
            }
        }

        return dt;
    }

    /**
     * Format date portion of a given date as yyyy-mm-dd
     */
    static formatDate(dt) {
        if (!dt) {
            return undefined;
        }

        let monthStr = this.lpad(("" + (dt.getMonth() + 1)), 2);
        let dayStr = this.lpad("" + dt.getDate(), 2);

        return dt.getFullYear() + "-" + monthStr + "-" + dayStr;
    }

    /**
     * Format time portion of a given data as HH:mm:ss
     */
    static formatTime(dt) {
        if (!dt) {
            return undefined;
        }

        let hrs = this.lpad("" + dt.getHours(), 2);
        let mins = this.lpad("" + dt.getMinutes(), 2);
        let secs = this.lpad("" + dt.getSeconds(), 2);

        return hrs + ":" + mins + ":" + secs;
    }

    /**
     * Format time portion of a given data as HH:mm:ss
     */
    static formatDateTime(dt) {
        if (!dt) {
            return undefined;
        }

        return this.formatDate(dt) + 'T' + this.formatTime(dt);
    }

    /**
     * Left pad a given string with a given character to a specified length.
     */
    static lpad(str, len, pad) {
        pad = typeof pad === "undefined" ? "0" : pad + "";
        str = str + "";
        while(str.length < len) {
            str = pad + str;
        }
        return str;
    }

    /**
     * Left pad a given numeric value with zeroes to a specified length.
     */
    static lpadn(n, len) {
        return this.lpad(n+'', len, '0')
    }

    /**
     * Extract a specified metric value from an array of metric
     * values based on a given metric id symbol.
     */
    static findMetricValue(metricValues, metricIdSymbol) {
        if (!metricValues) {
            return undefined;
        }

        let id = this.getSymbolPropName(metricIdSymbol);
        return metricValues.find((value) => {return (id === value.metricId)});
    }

    /**
     * Format a timestamp object returned from elasticsearch as yyyy-mm-dd hh:mm:ss
     */
    static formatTimeObject(timestamp) {
        let ts =
            timestamp.year + "-" +
            this.lpadn(timestamp.month, 2) + "-" +
            this.lpadn(timestamp.dayOfMonth, 2) + " " +
            this.lpadn(timestamp.hourOfDay, 2) + ":" +
            this.lpadn(timestamp.minute, 2) + ":" +
            this.lpadn(timestamp.second, 2);

        return ts;
    }

    /**
     * Create an array of option objects for a list of entities. Prepend
     * an 'All' option if specified.
     */
    static makeEntityOptions(entities, allOption, allOptionLabel) {
        let options = [];
        if (allOption) {
            if (allOptionLabel === undefined) {
                allOptionLabel = "All";
            }
            options.push({value: undefined, label: allOptionLabel});
        }

        return options.concat(entities.map(entity => {
            return {value: entity.id, label: entity.name}
        }));
    }

    /**
     * Get rid of any leading zeroes from a given number
     * @param num
     * @returns {string|XML|void|*}
     */
    static stripLeadZeros(num) {
        let val = ('' + num).replace(/^0+/, '');

        return val;
    }

    /**
     * Parse a string as an integer, ignoring any leading zeroes
     *
     * @param intStr The string to parse
     * @returns {Number} An integer
     */
    static parseIntStr(intStr) {
        let cleanStr = AppUtils.stripLeadZeros(intStr);

        // It must've only contained zeroes, so assume it's zero
        if (cleanStr.length == 0) {
            return 0;
        }

        let intVal = parseInt(cleanStr);
        return intVal;
    }

    static addArrayVal(arr, val) {
        let copy = [];
        copy = copy.concat(arr);
        let x = copy.find(v => {return v === val});
        if (!x) {
            copy.push(val);
        }

        return copy;
    }

    static rmvArrayVal(arr, val) {
        return arr.filter(v => {return v !== val})
    }

    /**
     * Parse a given date string into an object with granular date
     * values. The string is assumed to be in "YYYY-MM-dd HH:mm:ss" format
     */
    static parseDateValues(dtStr) {
        if (dtStr === null || dtStr === undefined || dtStr.length === 0) {
            return {};
        }

        let values = {};

        let dateTime = dtStr.split(' '); // Split date/time
        if (dateTime.length > 0) {
            let dateParts = dateTime[0].split('-');
            if (dateParts.length > 0) {
                values.year = AppUtils.parseIntStr(dateParts[0], 10);
                if (dateParts.length > 1) {
                    values.month = AppUtils.parseIntStr(dateParts[1], 10) - 1;
                    if (dateParts.length > 2) {
                        values.day = AppUtils.parseIntStr(dateParts[2], 10);
                    }
                }
            }

            if (dateTime.length > 1) {
                let timeParts = dateTime[1].split(':');
                if (timeParts.length > 0) {
                    values.hours = AppUtils.parseIntStr(timeParts[0], 10);
                    if (timeParts.length > 1) {
                        values.minutes = AppUtils.parseIntStr(timeParts[1], 10);
                        if (timeParts.length > 2) {
                            values.seconds = AppUtils.parseIntStr(timeParts[2], 10);
                        }
                    }
                }
            }
        }

        return values;
    }

    static drillIn(interval, intervalValue) {

        let parsedDate = moment.utc(intervalValue);

        // Next we consider the pieces w/respect to the current interval
        // to derive from/to dates of the form: YYYY-MM-DDTHH:MM:SS
        let fromDate = undefined;
        let toDate = undefined;

        if (interval === constants.YEAR_INTERVALS) {
            // Jan 1 of the year to 12/31 of the year
            fromDate = parsedDate.format('YYYY-MM-DD');
            toDate = parsedDate.add(1, 'years').format('YYYY-MM-DD');
        } else if (interval === constants.MONTH_INTERVALS) {
            // 1st of the month to last day of the month
            fromDate = parsedDate.format('YYYY-MM-DD');
            toDate = parsedDate.add(1, 'months').format('YYYY-MM-DD');
        } else if (interval === constants.WEEK_INTERVALS) {
            // Moment will return the monday of the specified week, so back up to sunday
            fromDate = parsedDate.add(-1, 'days').format('YYYY-MM-DD');
            toDate = parsedDate.add(7, 'days').format('YYYY-MM-DD');
        } else if (interval === constants.DAY_INTERVALS) {
            fromDate = parsedDate.format('YYYY-MM-DD');
            toDate = parsedDate.add(1, 'days').format('YYYY-MM-DD');
        } else if (interval === constants.HOUR_INTERVALS) {
            fromDate = parsedDate.format('YYYY-MM-DDTHH:00:00');
            toDate = parsedDate.add(1, 'hours').format('YYYY-MM-DDTHH');
        } else if (interval === constants.MINUTE_INTERVALS) {
            fromDate = parsedDate.format('YYYY-MM-DDTHH:mm');
            toDate = parsedDate.add(1, 'hours').format('YYYY-MM-DDTHH:mm');
        } else {
            fromDate = parsedDate.format('YYYY-MM-DDTHH:mm:ss');
            toDate = parsedDate.add(1, 'seconds').format('YYYY-MM-DDTHH:mm:ss');
        }

        // Return options corresponding to the requested drill-in
        return {
            interval:  AppUtils.getNextLowerInterval(interval),
            timeFrame: constants.DATERANGE_TIMEFRAME,
            fromDate: fromDate,
            toDate: toDate,
        };
    }

    static drillOut(interval, intervalValue) {

    }

    static getNextLowerInterval(interval) {
        if (interval === constants.YEAR_INTERVALS) {
            return constants.MONTH_INTERVALS;
        } else if (interval === constants.MONTH_INTERVALS) {
            return constants.WEEK_INTERVALS;
        } else if (interval === constants.WEEK_INTERVALS) {
            return constants.DAY_INTERVALS;
        } else if (interval === constants.DAY_INTERVALS) {
            return constants.HOUR_INTERVALS;
        } else {
            return constants.MINUTE_INTERVALS;
        }
    }

    static getNextHigherInterval(interval) {
        if (interval === constants.MONTH_INTERVALS) {
            return constants.YEAR_INTERVALS;
        } else if (interval === constants.WEEK_INTERVALS) {
            return constants.MONTH_INTERVALS;
        } else if (interval === constants.DAY_INTERVALS) {
            return constants.WEEK_INTERVALS;
        } else if (interval === constants.HOUR_INTERVALS) {
            return constants.DAY_INTERVALS;
        } else if (interval === constants.MINUTE_INTERVALS) {
            return constants.HOUR_INTERVALS;
        } else if (interval === constants.SECOND_INTERVALS) {
            return constants.MINUTE_INTERVALS;
        } else {
            return interval;
        }
    }

    static formatServiceArgs(options) {
        var argStr = "";
        var ct = 0;

        serviceUrlParams.forEach(p => {
            if (options[p]) {
                var sep = (ct === 0 ? "?" : "&");
                argStr = argStr + sep + (p + "=" + options[p]);
                ct = ct + 1;
            }
        });

        return argStr;
    }

    static translate(term, lang) {
        if (!term || term.length === 0) {
            return '';
        }

        let langMap = translations[lang];
        if (!langMap) {
            return term;
        }

        let trans = langMap[term];
        if (!trans) {
            if (term.includes(' ')) { // attempt to translate parts
                trans = '';
                let parts = term.split(' ');
                // If the last word is an integer, try to translate the prefix and prepend append to integer
                if (AppUtils.isIntStr(parts[parts.length - 1])) {
                    trans = AppUtils.translate(parts.slice(0, parts.length - 1).join(' '), lang) + ' ' + parts[parts.length - 1];
                } else {
                    // Try translating each part and put back together
                    parts.forEach(s => {
                        if (trans.length > 0) {
                            trans += ' ';
                        }
                        trans = trans + AppUtils.translate(s, lang);
                    });
                }
            } else { // default to given term
                trans = term;
            }
        }

        return trans;
    }

    static isIntStr(intStr) {
        return /^-{0,1}\d+$/.test(intStr);
    }

    static translateRefTypes(refTypes, lang) {
        return refTypes.map(refType => {
            return {value: refType.value, label: AppUtils.translate(refType.label, lang)}
        })
    }
    
    static getErroStatusAndText(argError){
        
        var returnObj={};
        
        if(argError != null){
            if((argError.response == null) ||  (argError.response == undefined)){
                returnObj.status=0;
                returnObj.statusText=argError.message;
            }else{
                if((argError.response.data != null) && (argError.response.data != undefined)){
                    returnObj.status=argError.response.status;
                    returnObj.statusText=argError.response.data.message;
                }else{
                    returnObj.status=argError.response.status;
                    returnObj.statusText="Some Error occured";
                }
            }
        }
        return returnObj;
    }//End of  getErroStatusAndText
    
    
    
    
}

export default AppUtils;

